import React, { useContext } from 'react';

import styled from '@emotion/styled';

import LargeCheckMarkIcon from '../Icons/LargeCheckMarkIcon';
import ModalContainer from './ModalContainer';
import { ModalContext } from './ModalContext';

interface ContainedPlainSuccessModalProps {
  successActionFn?: (...args: any[]) => void;
  description?: string;
  btnLabel: string;
  className?: string;
  hasCancelBtn?: boolean;
  title?: string;
  customIcon?: React.ReactNode;
}

const ContainedPlainSuccessModal: React.FC<ContainedPlainSuccessModalProps> = ({
  successActionFn,
  description,
  btnLabel,
  className,
  hasCancelBtn,
  title,
  customIcon,
}) => {
  const modalContext = useContext(ModalContext);

  const onClick = (e: any) => {
    modalContext?.setShowModal(false);
    successActionFn && successActionFn();
  };

  return (
    <ModalContainer>
      <SuccessViewWrapper>
        <SuccessView>
          <IconArea className={className ? className : ''}>
            {customIcon ?? <LargeCheckMarkIcon />}
          </IconArea>
          <Success>{title ?? 'Successful'}</Success>
          <SuccessDescription>
            {/* prefer explicitly passed description, fall back to context */}
            {description ?? modalContext?.successDescription}
          </SuccessDescription>
        </SuccessView>
        <BtnArea>
          {hasCancelBtn && (
            <CancelBtn onClick={() => modalContext?.setShowModal(false)}>
              Cancel
            </CancelBtn>
          )}
          <SuccessButton onClick={onClick}>{btnLabel}</SuccessButton>
        </BtnArea>
      </SuccessViewWrapper>
    </ModalContainer>
  );
};

export default ContainedPlainSuccessModal;

const SuccessViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  /* align-items: center; */
`;

const SuccessView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 400px;
  min-height: 289px;
  padding: 40px 18px;
  background-color: ${({ theme }) => theme.palette.pageBgColor};
`;

const Success = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.logirateBlack};
`;

const SuccessButton = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.palette.blueColor};
  padding: 14px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
  border-radius: 4px;
  cursor: pointer;
`;

const CancelBtn = styled.button`
  border: none;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 14px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.grayTextColor};
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
  border-radius: 4px;
  cursor: pointer;
`;

const IconArea = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 16px;
  background: rgba(91, 145, 245, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  & > svg {
    color: ${({ theme }) => theme.palette.blueColor};
  }

  &.pink {
    background-color: ${({ theme }) => theme.palette.logiratePrimaryLight};
  }

  &.pink > svg {
    color: ${({ theme }) => theme.palette.logiratePrimary};
  }

  &.transparent {
    background-color: transparent;
  }
`;

const SuccessDescription = styled.div`
  font-size: 14px;
  line-height: 25px;
  font-weight: 400;
  text-align: center;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const BtnArea = styled.div`
  display: flex;
  gap: 16px;
`;
